import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { BulkPlayerReadinessModal } from './BulkPlayerReadinessModal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface BulkPlayerReadinessButtonProps {
  gameId: string;
  teamId: number;
  teamSide: 'home' | 'away';
  teamName: string;
  gameDateTime?: string;
  disabled?: boolean;
}

interface PlayerData {
  player_id: string;
  player_type: 'supabase' | 'internal';
  firstName?: string;
  lastName?: string;
  username?: string;
  currentStatus?: string;
}

export const BulkPlayerReadinessButton: React.FC<BulkPlayerReadinessButtonProps> = ({
  gameId,
  teamId,
  teamSide,
  teamName,
  gameDateTime,
  disabled
}) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [players, setPlayers] = useState<PlayerData[]>([]);
  const [canSetReadiness, setCanSetReadiness] = useState(false);

  useEffect(() => {
    if (gameDateTime) {
      const checkTimeRestriction = () => {
        const gameTime = new Date(gameDateTime);
        const now = new Date();
        const hoursDiff = (gameTime.getTime() - now.getTime()) / (1000 * 60 * 60);
        setCanSetReadiness(hoursDiff <= 1);
      };

      checkTimeRestriction();
      const interval = setInterval(checkTimeRestriction, 60000);
      return () => clearInterval(interval);
    } else {
      setCanSetReadiness(true);
    }
  }, [gameDateTime]);

  const loadPlayers = async () => {
    try {
      // Get all attending players for this team
      const { data: participants, error: fetchError } = await supabase
        .from('game_participants')
        .select('player_id, player_type')
        .eq('game_id', gameId)
        .eq('team_side', teamSide)
        .in('participation_status', ['confirmed', 'attending']);

      if (fetchError) throw fetchError;

      if (!participants || participants.length === 0) {
        toast.info('No attending players found');
        return;
      }

      // Get player details and current readiness status
      const playerDetails = await Promise.all(
        participants.map(async (p) => {
          const playerData: PlayerData = {
            player_id: p.player_id,
            player_type: p.player_type as 'supabase' | 'internal',
          };

          // Fetch player info
          if (p.player_type === 'supabase') {
            const { data: profile } = await supabase
              .from('profiles')
              .select('first_name, last_name, username')
              .eq('id', p.player_id)
              .single();
            
            if (profile) {
              playerData.firstName = profile.first_name || undefined;
              playerData.lastName = profile.last_name || undefined;
              playerData.username = profile.username || undefined;
            }
          } else {
            const { data: internal } = await supabase
              .from('internal_users')
              .select('username')
              .eq('id', p.player_id)
              .single();
            
            if (internal) {
              playerData.username = internal.username || undefined;
            }
          }

          // Check current readiness status
          const { data: readiness } = await supabase
            .from('player_readiness_status')
            .select('readiness_status')
            .eq('game_id', gameId)
            .eq('player_id', p.player_id)
            .single();

          if (readiness) {
            playerData.currentStatus = readiness.readiness_status;
          }

          return playerData;
        })
      );

      setPlayers(playerDetails);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading players:', error);
      toast.error('Failed to load players');
    }
  };

  const handleMarkAllReady = async () => {
    setLoading(true);
    try {
      const user = await supabase.auth.getUser();
      
      // Only update players without a current status
      const playersToUpdate = players.filter(p => !p.currentStatus);

      if (playersToUpdate.length === 0) {
        toast.info('All players already have a readiness status');
        setShowModal(false);
        return;
      }

      // Update all players in parallel
      const updates = playersToUpdate.map(async (player) => {
        // Update player_readiness_status
        const { error: readinessError } = await supabase
          .from('player_readiness_status')
          .upsert({
            game_id: gameId,
            player_id: player.player_id,
            player_type: player.player_type,
            team_side: teamSide,
            readiness_status: 'present_ready_to_play',
            set_by: user.data.user?.id,
            set_by_type: 'supabase',
            set_by_role: 'coach',
            override_level: 1,
            notes: 'Bulk marked ready by coach'
          }, {
            onConflict: 'game_id,player_id'
          });

        if (readinessError) throw readinessError;

        // Update game_participants
        const { error: participantError } = await supabase
          .from('game_participants')
          .update({ participation_status: 'ready_to_play' })
          .eq('game_id', gameId)
          .eq('player_id', player.player_id)
          .eq('team_side', teamSide);

        if (participantError) throw participantError;
      });

      await Promise.all(updates);

      toast.success(`Successfully marked ${playersToUpdate.length} players as ready!`);
      setShowModal(false);
      window.location.reload();
    } catch (error) {
      console.error('Error marking all players ready:', error);
      toast.error('Failed to mark all players ready');
    } finally {
      setLoading(false);
    }
  };

  const buttonDisabled = disabled || !canSetReadiness || loading;

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="w-full">
              <Button
                onClick={loadPlayers}
                disabled={buttonDisabled}
                variant="outline"
                size="sm"
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : !canSetReadiness ? (
                  <>
                    <Clock className="mr-2 h-4 w-4" />
                    Mark All Players Ready
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Mark All Players Ready
                  </>
                )}
              </Button>
            </div>
          </TooltipTrigger>
          {!canSetReadiness && (
            <TooltipContent>
              <p>Available 1 hour before game time</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      <BulkPlayerReadinessModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleMarkAllReady}
        players={players}
        teamName={teamName}
        loading={loading}
      />
    </>
  );
};
